import { Badge, Button } from '../ui';
import { SWEEP_RUN_STATUSES, type SweepState } from '../../lib/keywordSweep';
import { formatRelativeDate } from '../../utils/dateUtils';

/**
 * The keyword sweep, in one line above the queue: when it last ran, what it cost in Ahrefs units,
 * what it proposed, and the two things a person does with it — run one now, or change its settings.
 */

interface SweepStripProps {
  clientName: string;
  state: SweepState | null;
  error: string | null;
  /** Run-now is on its way to the queue. */
  enqueueing: boolean;
  onRunNow: () => void;
  onOpenSettings: () => void;
  onOpenRules: () => void;
}

type RunCounts = { proposed_a?: number; proposed_b?: number };

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
const dot = <span aria-hidden style={faint}>·</span>;

export default function SweepStrip({ clientName, state, error, enqueueing, onRunNow, onOpenSettings, onOpenRules }: SweepStripProps) {
  const settingsButton = (
    <Button size="sm" variant="ghost" icon="settings" onClick={onOpenSettings}>
      Sweep settings
    </Button>
  );

  const shell = (children: React.ReactNode, actions: React.ReactNode) => (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-2) var(--space-3)',
        padding: '8px 12px',
        marginBottom: 'var(--space-3)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-sm)',
        color: 'var(--text-muted)',
      }}
    >
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        <span className="t-eyebrow">Keyword sweep</span>
        {children}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>{actions}</span>
    </div>
  );

  if (error) return shell(<span style={{ color: 'var(--danger-400)' }}>{error}</span>, settingsButton);
  if (!state) return shell(<span style={faint}>Loading...</span>, null);

  const { settings, lastRun, openJob, rules } = state;
  const rulesLink = (
    <button
      type="button"
      className="pressable"
      onClick={onOpenRules}
      style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 'var(--text-xs)' }}
    >
      {rules.length} deny {rules.length === 1 ? 'rule' : 'rules'}
    </button>
  );

  if (!settings) {
    return shell(
      <>
        <span>{clientName} isn’t set up for keyword sweeps yet.</span>
        {rules.length ? rulesLink : null}
      </>,
      settingsButton
    );
  }

  const status = lastRun ? SWEEP_RUN_STATUSES[lastRun.status] ?? { label: lastRun.status, tone: 'neutral' as const } : null;
  const counts = (lastRun?.counts ?? {}) as RunCounts;
  const runLabel = openJob ? (openJob.status === 'spec_queued' ? 'Sweep in the queue' : 'Sweep running') : enqueueing ? 'Queueing...' : 'Run sweep now';

  return shell(
    <>
      {lastRun ? (
        <>
          <span title={new Date(lastRun.started_at).toLocaleString()}>Last run {formatRelativeDate(lastRun.started_at)}</span>
          {status ? <Badge tone={status.tone}>{status.label}</Badge> : null}
          {dot}
          <span className="t-num" style={{ fontSize: 'var(--text-xs)' }} title="Ahrefs API units this run spent, against its cap">
            {lastRun.units_spent.toLocaleString()} / {lastRun.unit_cap.toLocaleString()} units
          </span>
          {counts.proposed_a != null || counts.proposed_b != null ? (
            <>
              {dot}
              <span className="t-num" style={{ fontSize: 'var(--text-xs)' }}>
                {counts.proposed_a ?? 0} A · {counts.proposed_b ?? 0} B
              </span>
            </>
          ) : null}
          {lastRun.dr != null ? (
            <>
              {dot}
              <span className="t-num" style={{ fontSize: 'var(--text-xs)' }} title="Domain rating at the time of the run, and the keyword difficulty it could reach">
                DR {lastRun.dr}
                {lastRun.kd_cap != null ? ` · KD cap ${lastRun.kd_cap}` : ''}
              </span>
            </>
          ) : null}
          {lastRun.status === 'failed' && lastRun.error ? (
            <span style={{ color: 'var(--danger-400)', fontSize: 'var(--text-xs)' }} title={lastRun.error}>
              {lastRun.error.length > 90 ? `${lastRun.error.slice(0, 90)}…` : lastRun.error}
            </span>
          ) : null}
        </>
      ) : (
        <span>No sweep yet.</span>
      )}
      {dot}
      <span style={{ fontSize: 'var(--text-xs)' }}>{settings.enabled ? `Monthly, on day ${settings.sweep_day}` : 'Monthly sweep off'}</span>
      {dot}
      {rulesLink}
    </>,
    <>
      {/* The title sits on a wrapper: a disabled button fires no hover, so its own tooltip never shows. */}
      <span title={openJob ? 'A sweep for this client hasn’t finished yet. Its proposals land here when it does.' : undefined}>
        <Button size="sm" variant="secondary" icon="play" onClick={onRunNow} disabled={!!openJob || enqueueing}>
          {runLabel}
        </Button>
      </span>
      {settingsButton}
    </>
  );
}
